import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { makeRequestAuth } from "../../../_config/api";

const BarChartLoan = () => {
    const [fetchedData, setFetchedData] = useState({ categories: [], series: [] });
    const [year, setYear] = useState("2026");
    const [status, setStatus] = useState("All");
    const [loanType, setLoanType] = useState("");

    const years = ["2025", "2026", "2027", "2028", "2029", "2030"];
    const statuses = ["All", "approved", "pending", "in_progress", "rejected"];
    const loanTypes = [
        { label: "All Types", value: "" },
        { label: "Home Loan", value: "home_loan" },
        { label: "Personal Loan", value: "personal_loan" },
        { label: "Business Loan", value: "business_loan" },
        { label: "Vehicle Loan", value: "vehicle_loan" },
        { label: "Education Loan", value: "education_loan" },
        { label: "Gold Loan", value: "gold_loan" },
        { label: "Loan Against Property", value: "loan_against_property" }
    ];

    useEffect(() => {
        const fetchChartData = async () => {
            try {
                const resData = { year: year, status: status === "All" ? "" : status, loanType: loanType };
                const response = await makeRequestAuth("post", "/admin/loanChart", resData);

                if (response?.code === 0 && !response.error && response?.data) {
                    setFetchedData(response.data);
                } else {
                    console.error("Server Error:", response?.data?.message || "Unknown error");
                }
            } catch (error) {
                console.error("Request Failed:", error);
            }
        };

        fetchChartData();
    }, [year, status, loanType]);

    const defaultCategories = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const chartCategories = Array.isArray(fetchedData?.categories) && fetchedData.categories.length > 0
        ? fetchedData.categories
        : defaultCategories;

    const chartSeries = Array.isArray(fetchedData?.series) && fetchedData.series.length > 0
        ? fetchedData.series.map(s => ({
            name: s.name,
            data: chartCategories.map((_, idx) => Number(s?.data?.[idx]) || 0)
          }))
        : [
            { name: "Approved", data: chartCategories.map(() => 0) },
            { name: "Pending", data: chartCategories.map(() => 0) },
            { name: "In Progress", data: chartCategories.map(() => 0) },
            { name: "Rejected", data: chartCategories.map(() => 0) }
          ];

    // Safe Max Value calculation for Y-Axis
    const categoryTotals = chartCategories.map((_, index) => {
        return chartSeries.reduce((sum, s) => sum + (Number(s?.data?.[index]) || 0), 0);
    });
    const calculatedMax = Math.max(...categoryTotals, 0);
    const maxVal = calculatedMax > 0 ? calculatedMax : 31;

    const chartOptions = {
        chart: {
            type: "bar",
            stacked: true, // Stacked bar mode
            toolbar: { show: false },
        },
        plotOptions: {
            bar: {
                columnWidth: "35%",
                borderRadius: 5,
            },
        },
        xaxis: {
            categories: chartCategories,
            labels: {
                rotate: 0,
                style: {
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#6b7280",
                },
            },
        },
        yaxis: {
            tickAmount: 4,
            min: 0,
            max: maxVal,
            labels: {
                formatter: (val) => {
                    if (val === undefined || val === null || isNaN(val)) return "0";
                    return Number.isInteger(val) ? `${val}` : `${val.toFixed(2)}`;
                },
                style: {
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#6b7280",
                },
            },
        },
        grid: {
            yaxis: { lines: { show: false } },
        },
        // Status Colors: Approved (Green), Pending (Orange), In Progress (Blue), Rejected (Red)
        colors: ["#10B981", "#F59E0B", "#3B82F6", "#EF4444"], 
        dataLabels: {
            enabled: false,
        },
        legend: {
            position: "top",
            horizontalAlign: "right",
            fontSize: "12px",
            fontWeight: 500,
            markers: {
                radius: 12
            }
        },
        tooltip: {
            theme: "light",
            shared: false,   // Single hover popup
            intersect: true, // Specific segment hover
            x: {
                formatter: (val, { dataPointIndex }) => `${chartCategories[dataPointIndex]} ${year}`
            },
            y: {
                formatter: (val, { seriesIndex }) => {
                    const statusNames = ["Approved", "Pending", "In Progress", "Rejected"];
                    const name = statusNames[seriesIndex] || "Requests";
                    return `${val || 0} ${name} Requests`;
                }
            }
        },
    };

    return (
        <div className="bg-white p-4 rounded-2xl shadow-md w-full mx-auto">
            <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
                <h2 className="text-sm font-semibold text-gray-700">
                    Total Loan Requests By Month
                </h2>

                <div className="flex gap-2 flex-wrap">
                    {/* 1. Year Filter */}
                    <select
                        className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-700"
                        value={year}
                        onChange={(e) => setYear(e.target.value)}
                    >
                        {years.map((y) => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>

                    {/* 2. Loan Type Filter */}
                    <select
                        className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-700"
                        value={loanType}
                        onChange={(e) => setLoanType(e.target.value)}
                    >
                        {loanTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                                {type.label}
                            </option>
                        ))}
                    </select>

                    {/* 3. Status Filter */}
                    <select
                        className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-700"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                    >
                        {statuses.map((s) => (
                            <option key={s} value={s}>
                                {s === "All" ? "All Statuses" : s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <Chart
                options={chartOptions}
                series={chartSeries}
                type="bar"
                height={235}
            />
        </div>
    );
};

export default BarChartLoan;
